import React, { useState, useEffect, useCallback } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * Reusable card/section wrapper with a clickable header that collapses the body.
 * The open/closed state is persisted per `storageKey` in localStorage so the user's
 * choice sticks across reloads (long deal lists, mailings, meetings, etc.).
 *
 * Props:
 *   storageKey    – unique key for localStorage persistence (e.g. "reviews.outreach")
 *   icon          – lucide icon component (rendered next to the title)
 *   iconClass     – tailwind color class for the icon (e.g. "text-sky-400")
 *   title         – section title
 *   count         – optional badge value on the right (number or string)
 *   right         – optional custom node on the right side (before the chevron)
 *   defaultOpen   – initial state if nothing is stored yet (default: true)
 *   className     – extra classes for the outer wrapper (border color etc.)
 *   headerClass   – extra classes for the header row
 *   bodyClass     – extra classes for the body wrapper (default has no padding)
 *   testId        – data-testid prefix
 *   children      – section body
 */
export function CollapsibleSection({
  storageKey,
  icon: Icon,
  iconClass = 'text-muted-foreground',
  title,
  count,
  right,
  defaultOpen = true,
  className = '',
  headerClass = '',
  bodyClass = '',
  testId,
  children,
}) {
  const lsKey = storageKey ? `collapse:${storageKey}` : null;
  const [open, setOpen] = useState(() => {
    if (!lsKey) return defaultOpen;
    try {
      const v = window.localStorage.getItem(lsKey);
      if (v === '0') return false;
      if (v === '1') return true;
    } catch (_) { /* ignore */ }
    return defaultOpen;
  });

  useEffect(() => {
    if (!lsKey) return;
    try { window.localStorage.setItem(lsKey, open ? '1' : '0'); } catch (_) { /* ignore */ }
  }, [lsKey, open]);

  const toggle = useCallback((e) => {
    // Ignore clicks that originated inside interactive elements in the header (e.g. the "right" slot).
    if (e && e.target && (e.target.closest('button, a, input, [role="button"], [data-collapsible-ignore]'))
        && !e.currentTarget.isSameNode(e.target)) {
      // Only ignore when it's a nested control; the outer button itself should still toggle.
      const outer = e.currentTarget;
      const t = e.target;
      if (!outer.isSameNode(t) && t.closest('[data-collapsible-ignore]')) return;
    }
    setOpen(v => !v);
  }, []);

  return (
    <div className={`bg-card border border-border rounded-sm ${className}`} data-testid={testId}>
      <div
        role="button"
        tabIndex={0}
        onClick={toggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggle(e); } }}
        aria-expanded={open}
        data-testid={testId ? `${testId}-toggle` : undefined}
        className={`w-full px-4 py-3 flex items-center gap-2 text-left cursor-pointer select-none ${open ? 'border-b border-border' : ''} hover:bg-secondary/30 transition-colors ${headerClass}`}
      >
        {Icon && <Icon className={`w-4 h-4 shrink-0 ${iconClass}`} />}
        <p className="font-heading text-base text-foreground flex-1 truncate">{title}</p>
        {right && <span data-collapsible-ignore>{right}</span>}
        {count !== undefined && count !== null && (
          <span className="text-[10px] text-muted-foreground font-mono">{count}</span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-muted-foreground/60 shrink-0 transition-transform ${open ? '' : '-rotate-90'}`}
        />
      </div>
      {open && <div className={bodyClass}>{children}</div>}
    </div>
  );
}

export default CollapsibleSection;
